import { useState, useRef, useEffect, useCallback } from 'react';
import { QrCode, Info, Copy, Check } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';

// View states for the overlay mode
const VIEW_STATES = {
  PREVIEW: 'preview',
  EXPANDED: 'expanded'
};

const AUTO_COLLAPSE_DELAY = 30000; // 30 seconds

const ExpoQRCode = ({ workspace, overlay = false }) => {
  const [viewState, setViewState] = useState(VIEW_STATES.PREVIEW);
  const [copied, setCopied] = useState(false);
  const componentRef = useRef(null);
  const autoCollapseTimeoutRef = useRef(null);

  const expoUrl = workspace?.subdomain ? `exp://ks-${workspace.subdomain}.ngrok.io` : null;

  // Copy to clipboard functionality
  const copyToClipboard = useCallback(async (e) => {
    e.stopPropagation();
    if (!expoUrl) return;

    try {
      await navigator.clipboard.writeText(expoUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL to clipboard:', err);
    }
  }, [expoUrl]);

  // Auto-collapse timer management
  const resetAutoCollapseTimer = useCallback(() => {
    if (autoCollapseTimeoutRef.current) {
      clearTimeout(autoCollapseTimeoutRef.current);
    }
    
    if (viewState === VIEW_STATES.EXPANDED) {
      autoCollapseTimeoutRef.current = setTimeout(() => {
        setViewState(VIEW_STATES.PREVIEW);
      }, AUTO_COLLAPSE_DELAY);
    }
  }, [viewState]);

  // Handle view state transitions
  const handleViewStateChange = useCallback((newState) => {
    setViewState(newState);
    if (newState === VIEW_STATES.EXPANDED) {
      resetAutoCollapseTimer();
    }
  }, [resetAutoCollapseTimer]);

  // Setup auto-collapse timer
  useEffect(() => {
    if (viewState === VIEW_STATES.EXPANDED) {
      resetAutoCollapseTimer();
    }
    return () => {
      if (autoCollapseTimeoutRef.current) {
        clearTimeout(autoCollapseTimeoutRef.current);
      }
    };
  }, [viewState, resetAutoCollapseTimer]);

  // Handle click outside for expanded view
  useEffect(() => {
    if (viewState !== VIEW_STATES.EXPANDED) return;

    const handleClickOutside = (event) => {
      if (componentRef.current && !componentRef.current.contains(event.target)) {
        setViewState(VIEW_STATES.PREVIEW);
      }
    };

    // Listen on main document
    document.addEventListener('mousedown', handleClickOutside);
    
    // Also listen on iframe document if it exists
    const iframe = document.querySelector('iframe');
    let iframeDoc = null;
    
    if (iframe) {
      try {
        iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDoc) {
          iframeDoc.addEventListener('mousedown', handleClickOutside);
        }
      } catch (e) {
        // Cross-origin iframe, can't access its document
        console.debug('Cannot access iframe document (likely cross-origin)');
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (iframeDoc) {
        iframeDoc.removeEventListener('mousedown', handleClickOutside);
      }
    };
  }, [viewState]);

  if (!workspace?.subdomain) return null;

  // Shared components
  const QRDisplay = ({ size = 120, containerClass = "" }) => (
    <div className={`relative bg-lime-100 dark:bg-lime-900/60 rounded-lg p-2 shadow-md ${containerClass}`}>
      <QRCodeCanvas
        value={expoUrl}
        size={size}
        level="M"
        includeMargin={false}
        className="block rounded-md overflow-hidden"
        fgColor="#84cc16"
        bgColor="#000000"
      />
    </div>
  );

  const Header = ({ size = 'normal' }) => {
    const sizes = {
      small: { icon: 16, text: 'text-xs' },
      normal: { icon: 16, text: 'text-sm' },
      large: { icon: 28, text: 'text-xl' }
    };
    const { icon, text } = sizes[size];
    
    return (
      <div className="flex items-center gap-2">
        <QrCode size={icon} className="text-lime-500" />
        <span className={`font-semibold text-neutral-800 dark:text-white ${text}`}>
          App Preview
        </span>
      </div>
    );
  };

  // Overlay mode rendering
  if (overlay) {
    const overlayContainerClass = "relative bg-white dark:bg-neutral-900 border border-neutral-200/50 dark:border-neutral-700/50 shadow-lg hover:shadow-xl dark:shadow-black/20 transition-all duration-150 ease-out";
    

    // Preview state - hover preview
    if (viewState === VIEW_STATES.PREVIEW) {
      return (
        <div 
          ref={componentRef}
          className={`${overlayContainerClass} cursor-pointer rounded-xl p-3 group`}
          onClick={() => handleViewStateChange(VIEW_STATES.EXPANDED)}
          onMouseEnter={resetAutoCollapseTimer}
          onMouseMove={resetAutoCollapseTimer}
          onMouseLeave={() => handleViewStateChange(VIEW_STATES.PREVIEW)}
        >
          <div className="flex flex-col items-center gap-3">
            <Header size="small" />
            <QRDisplay size={72} containerClass="shadow-sm" />
            <div className="flex items-center gap-1 opacity-75 group-hover:opacity-100 transition-opacity">
              <Info size={12} className="text-lime-500" />
              <span className="text-xs text-neutral-600 dark:text-neutral-300 group-hover:underline transition-all">More Info</span>
            </div>
          </div>
        </div>
      );
    }

    // Expanded state - full details
    if (viewState === VIEW_STATES.EXPANDED) {
      return (
        <div 
          ref={componentRef}
          className={`${overlayContainerClass} rounded-2xl p-5 min-w-[280px] max-w-[320px] cursor-pointer`}
          onMouseEnter={resetAutoCollapseTimer}
          onMouseMove={resetAutoCollapseTimer}
          onClick={(e) => {
            e.stopPropagation();
            handleViewStateChange(VIEW_STATES.PREVIEW);
          }}
        >
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-lime-50/30 via-transparent to-emerald-50/20 dark:from-lime-900/10 dark:via-transparent dark:to-emerald-900/10 rounded-2xl pointer-events-none" />

          <div className="relative flex flex-col items-center gap-4">
            <Header />
            <QRDisplay size={120} containerClass="shadow-md" />
            
            {/* Quick instruction */}
            <div className="text-center">
              <p className="text-sm font-medium text-neutral-800 dark:text-white mb-1">
                Scan with your camera
              </p>
              <p className="text-xs text-neutral-600 dark:text-neutral-400">
                Opens directly in Expo Go app
              </p>
            </div>
            
            {/* Step-by-step guide */}
            <div className="w-full bg-gradient-to-r from-lime-50 to-emerald-50 dark:from-lime-950/50 dark:to-emerald-950/50 rounded-lg p-3 border border-lime-200/50 dark:border-lime-800/50">
              {[
                { step: 1, text: (
                  <>
                    Install{' '}
                    <a 
                      href="https://expo.dev/go" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-lime-600 dark:text-lime-400 hover:text-lime-700 dark:hover:text-lime-300 font-medium underline decoration-dotted transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Expo Go
                    </a>
                    {' '}from your app store
                  </>
                )},
                { step: 2, text: "Scan QR code to preview your app" }
              ].map(({ step, text }) => (
                <div key={step} className="flex items-start gap-2 mb-2 last:mb-0">
                  <div className="w-5 h-5 bg-lime-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs text-white font-bold">{step}</span>
                  </div>
                  <p className="text-xs text-neutral-700 dark:text-neutral-300 leading-relaxed">
                    {text}
                  </p>
                </div>
              ))}
            </div>

            {/* Or separator */}
            <div className="flex items-center w-full">
              <div className="flex-1 h-px bg-neutral-200 dark:bg-neutral-700"></div>
              <span className="px-3 text-xs text-neutral-500 dark:text-neutral-400 font-medium">OR</span>
              <div className="flex-1 h-px bg-neutral-200 dark:bg-neutral-700"></div>
            </div>

            {/* URL Display with Copy */}
            <div className="w-full bg-neutral-50 dark:bg-neutral-800 rounded-lg p-3 border border-neutral-200 dark:border-neutral-700">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Expo URL</p>
                  <p className="text-sm font-mono text-neutral-800 dark:text-white truncate">
                    {expoUrl}
                  </p>
                </div>
                <button
                  onClick={copyToClipboard}
                  className="flex-shrink-0 p-2 rounded-md bg-lime-100 dark:bg-lime-900/50 hover:bg-lime-200 dark:hover:bg-lime-900 transition-colors"
                  title={copied ? "Copied!" : "Copy link"}
                >
                  {copied ? (
                    <Check size={16} className="text-lime-600 dark:text-lime-400" />
                  ) : (
                    <Copy size={16} className="text-lime-600 dark:text-lime-400" />
                  )}
                </button>
              </div>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">
                Open this link in your phone's browser where Expo Go is installed
              </p>
            </div>
          </div>
        </div>
      );
    }
  }

  return null;
};

export default ExpoQRCode;